import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@gravity-ui/icons';
import 'bootstrap/dist/css/bootstrap.min.css';

import AdminBreadcrumbNav from '../../components/AdminBreadcrumbNav/AdminBreadcrumbNav';
import AdminItemGrid from '../../components/AdminItemGrid/AdminItemGrid';
import AdminModalForm from '../../components/AdminModalForm/AdminModalForm';
import AccessGrantsModal from '../../components/AccessGrantsModal/AccessGrantsModal';
import {
  getDefaultConfigByCameraType,
  getStatusColor,
  getStatusText,
  isPeopleLikeCameraType,
  levelLabels,
  levelMap,
} from '../../constants/adminPageConstants';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useNavigationState } from '../../hooks/useNavigationState';
import {
  cancelDeleteCamera,
  createCamera,
  deleteCamera,
  getCamera,
  getLastFrame,
  listCameras,
  restartCamera,
  startCamera,
  stopCamera,
  updateCamera,
} from '../../services/cameras';
import {
  createFactory,
  deleteFactory,
  listFactories,
  updateFactory,
} from '../../services/factories';
import {
  createRoom,
  deleteRoom,
  listRooms,
  updateRoom,
} from '../../services/rooms';
import {
  createSection,
  deleteSection,
  listSections,
  updateSection,
} from '../../services/sections';
import {
  createWorkshop,
  deleteWorkshop,
  listWorkshops,
  updateWorkshop,
} from '../../services/workshops';
import { getUsers } from '../../services/auth';
import { getVideoSettings, saveVideoSettings, startArchive } from '../../services/liveArchive';
import { describeHttpError } from '../../utils/apiError';
import { deleteEntityImage, describeEntityImageError, uploadEntityImage } from '../../services/entityImages';
import { describeConflict } from '../../utils/conflictMessages';
import {
  detectionConfigsFormText,
  stripZoneKeysFromDetectionConfigs,
} from '../../utils/detectionConfigs';
import { buildCameraUpdatePayload } from '../../utils/cameraUpdatePayload';
import { openCameraFramesFromAdmin, openLocationInCameras } from '../../utils/camerasDrilldown';
import { useNavigationEntitlements } from '../../hooks/useNavigationEntitlements';
import { isNavigationKeyAllowed, NAV_KEYS } from '../../utils/navigationEntitlements';
import { resolveCameraCardPreviewUrl } from '../../utils/cameraPreview';
import CameraStatusFilter from '../../components/CameraStatusFilter/CameraStatusFilter';
import useCameraStatusFilter from '../../hooks/useCameraStatusFilter';
import { ALL_CAMERA_STATUS_GROUPS, filterCamerasByStatusGroups } from '../../utils/cameraStatusFilter';
import "./AdminPage.css";

const extractLastFrame = (result) => {
  if (!result) return null;
  if (Array.isArray(result)) return result[0] ?? null;
  if (typeof result === 'object' && 'items' in result) return result.items?.[0] ?? null;
  return null;
};

const getDefaultLiveArchiveFields = () => ({
  live_enabled: false,
  live_rtsp_url: '',
  live_max_readers: 2,
  live_rtsp_transport: 'tcp',
  archive_enabled: false,
  archive_rtsp_url: '',
  archive_record_format: 'fmp4',
  archive_retention_days: 7,
  hot_buffer_retention_hours: 24,
  archive_segment_duration_minutes: 10,
  archive_priority: 'normal',
  archive_timezone: 'Europe/Moscow',
  archive_status: 'disabled',
  archive_pause_reason: null,
  can_resume_archive: false,
  masked_live_rtsp_url: '',
  masked_archive_rtsp_url: '',
  has_live_rtsp_url: false,
  has_archive_rtsp_url: false,
});

// ТЗ №2 §10.1 (решение Q2): video backend по-прежнему хранит archive_timezone,
// поэтому отправляем туда общий display_timezone камеры — второго поля в UI нет.
const buildLiveArchivePayload = (data, shouldIncludeRtspUrl) => {
  const payload = {
    live_enabled: Boolean(data.live_enabled),
    live_max_readers: parseInt(data.live_max_readers, 10) || 0,
    live_rtsp_transport: data.live_rtsp_transport || 'tcp',
    archive_enabled: Boolean(data.archive_enabled),
    archive_record_format: data.archive_record_format || 'fmp4',
    archive_retention_days: parseInt(data.archive_retention_days, 10) || 0,
    hot_buffer_retention_hours: parseInt(data.hot_buffer_retention_hours, 10) || 0,
    archive_segment_duration_minutes: parseInt(data.archive_segment_duration_minutes, 10) || 0,
    archive_priority: data.archive_priority || 'normal',
    archive_timezone: data.display_timezone || data.archive_timezone || 'Europe/Moscow',
  };

  // live_rtsp_url/archive_rtsp_url fields are prefilled with the masked
  // placeholder (contains "*"); only resend them when the admin actually
  // typed a real value, otherwise we'd overwrite the real stored RTSP URL
  // on the backend with the literal masked string.
  if (payload.live_enabled && shouldIncludeRtspUrl(data.live_rtsp_url)) {
    payload.live_rtsp_url = data.live_rtsp_url.trim();
  }
  if (payload.archive_enabled && shouldIncludeRtspUrl(data.archive_rtsp_url)) {
    payload.archive_rtsp_url = data.archive_rtsp_url.trim();
  }

  return payload;
};

const normalizeVideoSettings = (settings) => ({
  ...getDefaultLiveArchiveFields(),
  live_enabled: Boolean(settings?.live_enabled),
  archive_enabled: Boolean(settings?.archive_enabled),
  live_max_readers: settings?.live_max_readers ?? 2,
  live_rtsp_transport: settings?.live_rtsp_transport || 'tcp',
  archive_retention_days: settings?.archive_retention_days ?? 7,
  hot_buffer_retention_hours: settings?.hot_buffer_retention_hours ?? 24,
  archive_segment_duration_minutes: settings?.archive_segment_duration_minutes ?? 10,
  archive_record_format: settings?.archive_record_format || 'fmp4',
  archive_priority: settings?.archive_priority || 'normal',
  archive_status: settings?.archive_status || (settings?.archive_enabled ? 'active' : 'disabled'),
  archive_pause_reason: settings?.archive_pause_reason || null,
  can_resume_archive: Boolean(settings?.can_resume_archive),
  archive_timezone: settings?.archive_timezone || 'Europe/Moscow',
  has_live_rtsp_url: Boolean(settings?.has_live_rtsp_url || settings?.masked_live_rtsp_url || settings?.live_rtsp_url_masked),
  has_archive_rtsp_url: Boolean(settings?.has_archive_rtsp_url || settings?.masked_archive_rtsp_url || settings?.archive_rtsp_url_masked),
  masked_live_rtsp_url: settings?.masked_live_rtsp_url || settings?.live_rtsp_url_masked || '',
  masked_archive_rtsp_url: settings?.masked_archive_rtsp_url || settings?.archive_rtsp_url_masked || '',
  live_rtsp_url: settings?.masked_live_rtsp_url || settings?.live_rtsp_url_masked || '',
  archive_rtsp_url: settings?.masked_archive_rtsp_url || settings?.archive_rtsp_url_masked || '',
});

const hasImageChange = (data) => Boolean(data?.image_file || data?.image_remove);

const applyImageChange = (entityType, entityId, data) => (data.image_file
  ? uploadEntityImage(entityType, entityId, data.image_file)
  : deleteEntityImage(entityType, entityId));

const AdminPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLevel, setCurrentLevel, selectedItems, setSelectedItems } =
    useNavigationState('adminPage_nav');
  // ТЗ №26, FE-CORR-07 FE-002/003: переход из «Камер» кладёт в history state,
  // какую карточку открыть на правку (adminEdit) или подсветить
  // (adminHighlight) после загрузки уровня.
  const pendingEditRef = useRef(
    location.state?.adminEdit
      ? { ...location.state.adminEdit, mode: 'edit' }
      : location.state?.adminHighlight
        ? { ...location.state.adminHighlight, mode: 'highlight' }
        : null
  );
  const [highlight, setHighlight] = useState(null);
  const [targetNotice, setTargetNotice] = useState(null);
  const [levelLoaded, setLevelLoaded] = useState(false);
  const { keys: navigationKeys } = useNavigationEntitlements();
  const { validateForm, shouldIncludeRtspUrl } = useFormValidation();

  const [currentData, setCurrentData] = useState([]);
  const [loading, setLoading] = useState(false);
  // ТЗ V5 №14: фильтр состояний камер — свой для каждой локации и пользователя.
  const statusFilterRoomId = currentLevel === 'cameras' ? selectedItems.room?.id ?? null : null;
  const [statusGroups, setStatusGroups] = useCameraStatusFilter(statusFilterRoomId);
  const cameraFilterActive = statusFilterRoomId != null;
  const visibleData = useMemo(() => (
    cameraFilterActive ? filterCamerasByStatusGroups(currentData, statusGroups) : currentData
  ), [cameraFilterActive, currentData, statusGroups]);
  const [cameraImages, setCameraImages] = useState({});
  const [availableOwners, setAvailableOwners] = useState([]);

  const ownerMap = useMemo(() => {
    const map = {};
    availableOwners.forEach((u) => {
      map[u.id] = u.email ? u.email.split('@')[0] : u.username || u.id;
    });
    return map;
  }, [availableOwners]);
  const [managingCamera, setManagingCamera] = useState(null);
  const [restoringCamera, setRestoringCamera] = useState(null);
  const [modal, setModal] = useState({
    isOpen: false,
    mode: 'create',
    level: null,
    data: null,
    loading: false,
    errors: {},
  });

  const cacheRef = useRef({
    factories: null,
    workshops: new Map(),
    sections: new Map(),
    rooms: new Map(),
    cameras: new Map(),
  });
  const previewCacheRef = useRef(new Map());
  const previewRequestRef = useRef(new Set());

  useEffect(() => {
    let mounted = true;
    getUsers({ limit: 200 })
      .then((res) => {
        const users = Array.isArray(res) ? res : res.data || [];
        if (mounted) setAvailableOwners(users);
      })
      .catch(() => {
        if (mounted) setAvailableOwners([]);
      });
    return () => { mounted = false; };
  }, []);

  const getParentId = () => {
    switch (currentLevel) {
      case 'workshops':
        return selectedItems.factory?.id;
      case 'sections':
        return selectedItems.workshop?.id;
      case 'rooms':
        return selectedItems.section?.id;
      case 'cameras':
        return selectedItems.room?.id;
      default:
        return null;
    }
  };

  const reloadCurrentLevelData = async () => {
    if (currentLevel === 'factories') {
      const data = await listFactories();
      cacheRef.current.factories = data;
      setCurrentData(data);
      return;
    }

    if (currentLevel === 'workshops' && selectedItems.factory) {
      const data = await listWorkshops(selectedItems.factory.id);
      cacheRef.current.workshops.set(selectedItems.factory.id, data);
      setCurrentData(data);
      return;
    }

    if (currentLevel === 'sections' && selectedItems.workshop) {
      const data = await listSections(selectedItems.workshop.id);
      cacheRef.current.sections.set(selectedItems.workshop.id, data);
      setCurrentData(data);
      return;
    }

    if (currentLevel === 'rooms' && selectedItems.section) {
      const data = await listRooms(selectedItems.section.id);
      cacheRef.current.rooms.set(selectedItems.section.id, data);
      setCurrentData(data);
      return;
    }

    if (currentLevel === 'cameras' && selectedItems.room) {
      const data = await listCameras(selectedItems.room.id);
      cacheRef.current.cameras.set(selectedItems.room.id, data);
      setCurrentData(data);
    }
  };

  // Превью карточки — свежий последний кадр; до его загрузки AdminItemGrid
  // показывает original из самой камеры (он уже в списке камер).
  const loadCameraPreviewsOnce = useCallback(async (cameras) => {
    const missing = cameras.filter((camera) => (
      !previewCacheRef.current.has(camera.id) &&
      !previewRequestRef.current.has(camera.id)
    ));

    if (!missing.length) return;

    missing.forEach((camera) => previewRequestRef.current.add(camera.id));

    const results = await Promise.all(
      missing.map(async (camera) => {
        try {
          const result = await getLastFrame(camera.id);
          return { id: camera.id, url: resolveCameraCardPreviewUrl(camera, extractLastFrame(result)) || null };
        } catch {
          return { id: camera.id, url: resolveCameraCardPreviewUrl(camera, null) || null };
        }
      }),
    );

    const nextImages = {};
    results.forEach(({ id, url }) => {
      previewCacheRef.current.set(id, url);
      previewRequestRef.current.delete(id);
      if (url) {
        nextImages[id] = url;
      }
    });

    if (Object.keys(nextImages).length > 0) {
      setCameraImages((prev) => ({ ...prev, ...nextImages }));
    }
  }, []);

  const handleCameraAction = async (cameraId, action) => {
    setManagingCamera(cameraId);

    try {
      if (action === 'start') await startCamera(cameraId);
      if (action === 'stop') await stopCamera(cameraId);
      if (action === 'restart') await restartCamera(cameraId);

      const updated = await getCamera(cameraId);
      setCurrentData((prev) => prev.map((camera) => (camera.id === cameraId ? updated : camera)));
      alert(`Команда «${action}» отправлена. Сейчас камера в состоянии «${getStatusText(updated.status)}».`);
    } catch {
      try {
        const current = await getCamera(cameraId);
        setCurrentData((prev) => prev.map((camera) => (camera.id === cameraId ? current : camera)));
      } catch (error) {
        console.log(error);
      }
    } finally {
      setManagingCamera(null);
    }
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setLevelLoaded(false);

      try {
        if (currentLevel === 'factories') {
          let data = cacheRef.current.factories;
          if (!data) {
            data = await listFactories();
            cacheRef.current.factories = data;
          }
          if (mounted) setCurrentData(data);
        }

        if (currentLevel === 'workshops' && selectedItems.factory) {
          const id = selectedItems.factory.id;
          let data = cacheRef.current.workshops.get(id);
          if (!data) {
            data = await listWorkshops(id);
            cacheRef.current.workshops.set(id, data);
          }
          if (mounted) setCurrentData(data);
        }

        if (currentLevel === 'sections' && selectedItems.workshop) {
          const id = selectedItems.workshop.id;
          let data = cacheRef.current.sections.get(id);
          if (!data) {
            data = await listSections(id);
            cacheRef.current.sections.set(id, data);
          }
          if (mounted) setCurrentData(data);
        }

        if (currentLevel === 'rooms' && selectedItems.section) {
          const id = selectedItems.section.id;
          let data = cacheRef.current.rooms.get(id);
          if (!data) {
            data = await listRooms(id);
            cacheRef.current.rooms.set(id, data);
          }
          if (mounted) setCurrentData(data);
        }

        if (currentLevel === 'cameras' && selectedItems.room) {
          const id = selectedItems.room.id;
          let data = cacheRef.current.cameras.get(id);
          if (!data) {
            data = await listCameras(id);
            cacheRef.current.cameras.set(id, data);
          }

          if (mounted) {
            setCurrentData(data);
            const cachedImages = {};
            data.forEach((camera) => {
              const cachedUrl = previewCacheRef.current.get(camera.id);
              if (cachedUrl) {
                cachedImages[camera.id] = cachedUrl;
              }
            });
            if (Object.keys(cachedImages).length > 0) {
              setCameraImages((prev) => ({ ...prev, ...cachedImages }));
            }
            loadCameraPreviewsOnce(data);
          }
        }
      } catch (error) {
        console.error('Ошибка загрузки данных', error);
        // 403/404 и прочие сбои: список уровня недоступен, прежний не показываем.
        if (mounted) setCurrentData([]);
      } finally {
        if (mounted) {
          setLoading(false);
          setLevelLoaded(true);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [
    currentLevel,
    loadCameraPreviewsOnce,
    selectedItems.factory,
    selectedItems.workshop,
    selectedItems.section,
    selectedItems.room,
  ]);

  const handleItemClick = (item, level) => {
    const next = { ...selectedItems };

    switch (level) {
      case 'factory':
        next.factory = item;
        next.workshop = null;
        next.section = null;
        next.room = null;
        next.camera = null;
        setCurrentLevel('workshops');
        break;
      case 'workshop':
        next.workshop = item;
        next.section = null;
        next.room = null;
        next.camera = null;
        setCurrentLevel('sections');
        break;
      case 'section':
        next.section = item;
        next.room = null;
        next.camera = null;
        setCurrentLevel('rooms');
        break;
      case 'room':
        next.room = item;
        next.camera = null;
        setCurrentLevel('cameras');
        break;
      default:
        return;
    }

    setSelectedItems(next);
  };

  const handleBreadcrumbClick = (level) => {
    switch (level) {
      case 'factories':
        setSelectedItems({ factory: null, workshop: null, section: null, room: null, camera: null });
        break;
      case 'workshops':
        setSelectedItems({ ...selectedItems, workshop: null, section: null, room: null, camera: null });
        break;
      case 'sections':
        setSelectedItems({ ...selectedItems, section: null, room: null, camera: null });
        break;
      case 'rooms':
        setSelectedItems({ ...selectedItems, room: null, camera: null });
        break;
      default:
        return;
    }

    setCurrentLevel(level);
  };

  const handleBack = () => handleBreadcrumbClick({
    cameras: 'rooms',
    rooms: 'sections',
    sections: 'workshops',
    workshops: 'factories',
  }[currentLevel]);

  const handleCreateClick = () => {
    const initialType = 'EQUIPMENT';
    const initialConfig = getDefaultConfigByCameraType(initialType);
    const baseData = { name: '', owner: '' };

    const initialData = currentLevel === 'cameras'
      ? {
        ...baseData,
        ...getDefaultLiveArchiveFields(),
        rtsp_url: '',
        frame_interval_sec: 0,
        retention_minutes: 0,
        slow_retry_interval_minutes: 15,
        display_timezone: 'Europe/Moscow',
        detection_configs: JSON.stringify(initialConfig, null, 2),
        type: initialType,
        shifts: [],
      }
      : {
        ...baseData,
        image_url: '',
      };

    setModal({
      isOpen: true,
      mode: 'create',
      level: currentLevel,
      data: initialData,
      loading: false,
      errors: {},
    });
  };

  const handleEditClick = async (item, event) => {
    event?.stopPropagation();

    try {
      let entityData = item;

      if (currentLevel === 'cameras') {
        const loaded = await getCamera(item.id);
        if (loaded) {
          entityData = loaded;
        }
      }

      let formData;
      if (currentLevel === 'cameras') {
        // ADMIN-ZONE-SYNC01: zones/monitoring_zone из detection_configs камеры
        // вырезаются ещё до показа в textarea — канонический источник зон
        // теперь Zones API (ZoneEditor), а не это поле формы.
        // ТЗ №33: ключи детекторов тоже не показываются — их меняет только
        // профиль детекции; при сохранении уходят значения самой камеры.
        // Конфигурация может прийти строкой или объектом; если её нет или она
        // нечитаема — шаблон типа, тоже без зон и детекторов.
        const detectionConfigsText = detectionConfigsFormText(
          entityData.detection_configs,
          getDefaultConfigByCameraType(entityData.type || 'EQUIPMENT'),
        );

        const shifts = [];
        if (entityData.shift1_start && entityData.shift1_end) {
          shifts.push({
            start: entityData.shift1_start.slice(0, 5),
            end: entityData.shift1_end.slice(0, 5),
          });
        }
        if (entityData.shift2_start && entityData.shift2_end) {
          shifts.push({
            start: entityData.shift2_start.slice(0, 5),
            end: entityData.shift2_end.slice(0, 5),
          });
        }
        if (entityData.shift3_start && entityData.shift3_end) {
          shifts.push({
            start: entityData.shift3_start.slice(0, 5),
            end: entityData.shift3_end.slice(0, 5),
          });
        }

        let videoSettings = getDefaultLiveArchiveFields();
        try {
          videoSettings = normalizeVideoSettings(await getVideoSettings(entityData.id));
        } catch {
          videoSettings = normalizeVideoSettings(entityData);
        }

        formData = {
          camera_id: entityData.id,
          name: entityData.name || '',
          rtsp_url: entityData.rtsp_url || '',
          frame_interval_sec: entityData.frame_interval_sec,
          retention_minutes: entityData.retention_minutes,
          slow_retry_interval_minutes: entityData.slow_retry_interval_minutes ?? 15,
          display_timezone: entityData.display_timezone || 'Europe/Moscow',
          detection_configs: detectionConfigsText,
          type: entityData.type || 'EQUIPMENT',
          owner: entityData.owner || '',
          shifts,
          ...videoSettings,
        };
      } else {
        formData = {
          name: entityData.name || '',
          image_url: entityData.image || '',
          owner: entityData.owner || '',
        };
      }

      setModal({
        isOpen: true,
        mode: 'edit',
        level: currentLevel,
        data: formData,
        originalItem: entityData,
        loading: false,
        errors: {},
      });
    } catch {
      console.warn('Не удалось загрузить данные для редактирования');
    }
  };

  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleFormChange = (event) => {
    const { name, type, value, checked } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;

    setModal((prev) => ({ ...prev, data: { ...prev.data, [name]: nextValue } }));
    if (modal.errors[name]) {
      setModal((prev) => ({ ...prev, errors: { ...prev.errors, [name]: undefined } }));
    }
  };

  const handleShiftChange = (index, field, value) => {
    const newShifts = [...(modal.data.shifts || [])];
    if (!newShifts[index]) {
      newShifts[index] = { start: '', end: '' };
    }
    newShifts[index][field] = value;

    const errorKey = `shift_${index}`;
    setModal((prev) => {
      const newErrors = { ...prev.errors };
      delete newErrors[errorKey];

      return {
        ...prev,
        data: { ...prev.data, shifts: newShifts },
        errors: newErrors,
      };
    });
  };

  const handleAddShift = () => {
    const currentShifts = modal.data.shifts || [];
    if (currentShifts.length >= 3) return;

    setModal((prev) => ({
      ...prev,
      data: { ...prev.data, shifts: [...currentShifts, { start: '', end: '' }] },
    }));
  };

  const handleRemoveShift = (index) => {
    const newShifts = (modal.data.shifts || []).filter((_, shiftIndex) => shiftIndex !== index);
    setModal((prev) => ({ ...prev, data: { ...prev.data, shifts: newShifts } }));
  };

  const handleResumeArchive = async () => {
    const cameraId = modal.originalItem?.id;
    if (!cameraId) return;

    setModal((prev) => ({ ...prev, loading: true }));
    try {
      await startArchive(cameraId);
      const settings = normalizeVideoSettings(await getVideoSettings(cameraId));
      setModal((prev) => ({
        ...prev,
        data: { ...prev.data, ...settings },
        loading: false,
      }));
    } catch {
      setModal((prev) => ({ ...prev, loading: false }));
      alert('Не удалось возобновить запись архива');
    }
  };

  const handleTypeChange = (event) => {
    const newType = event.target.value;

    setModal((prev) => {
      const newConfig = getDefaultConfigByCameraType(newType);
      // В правке камеры детекторы из шаблона типа не показываются: сохранение
      // всё равно отправит детекторы самой камеры (ТЗ №33).
      const configText = prev.mode === 'edit'
        ? detectionConfigsFormText(newConfig)
        : JSON.stringify(newConfig, null, 2);
      return {
        ...prev,
        data: {
          ...prev.data,
          type: newType,
          detection_configs: configText,
          shifts: isPeopleLikeCameraType(newType) ? prev.data.shifts : [],
        },
      };
    });
  };

  const handleModalSubmit = async (event) => {
    event.preventDefault();

    const errors = validateForm({
      data: modal.data,
      level: modal.level,
      mode: modal.mode,
    });
    if (Object.keys(errors).length > 0) {
      setModal((prev) => ({ ...prev, errors }));
      return;
    }

    setModal((prev) => ({ ...prev, loading: true, errors: { ...prev.errors, form: undefined } }));

    try {
      const parentId = getParentId();
      let refetchCameraId = null;
      // ТЗ V5 №12: id сохранённой сущности нужен второму шагу — загрузке файла.
      let savedEntity = modal.mode === 'edit' ? modal.originalItem : null;

      if (modal.mode === 'create') {
        switch (modal.level) {
          case 'factories':
            savedEntity = await createFactory({
              name: modal.data.name,
              image: modal.data.image_url,
              owner: modal.data.owner,
            });
            cacheRef.current.factories = null;
            break;
          case 'workshops':
            savedEntity = await createWorkshop(parentId, {
              name: modal.data.name,
              image: modal.data.image_url,
              owner: modal.data.owner,
            });
            cacheRef.current.workshops.delete(parentId);
            break;
          case 'sections':
            savedEntity = await createSection(parentId, {
              name: modal.data.name,
              image: modal.data.image_url,
              owner: modal.data.owner,
            });
            cacheRef.current.sections.delete(parentId);
            break;
          case 'rooms':
            savedEntity = await createRoom(parentId, {
              name: modal.data.name,
              image: modal.data.image_url,
              owner: modal.data.owner,
            });
            cacheRef.current.rooms.delete(parentId);
            break;
          case 'cameras': {
            // ADMIN-ZONE-SYNC01: zones/monitoring_zone из формы никогда не
            // уходят на бэкенд — канонический источник зон камеры теперь
            // Zones API, а monitoring_zone бэкенд проецирует сам.
            const detectionConfigsPayload = modal.data.detection_configs
              ? stripZoneKeysFromDetectionConfigs(modal.data.detection_configs)
              : JSON.stringify(getDefaultConfigByCameraType(modal.data.type));

            const shifts = modal.data.shifts || [];
            const payload = {
              room_id: parentId,
              name: modal.data.name.trim(),
              frame_interval_sec: parseInt(modal.data.frame_interval_sec, 10),
              retention_minutes: parseInt(modal.data.retention_minutes, 10),
              slow_retry_interval_minutes: parseInt(modal.data.slow_retry_interval_minutes, 10),
              display_timezone: modal.data.display_timezone,
              detection_configs: detectionConfigsPayload,
              type: modal.data.type,
              owner: modal.data.owner,
              shift1_start: shifts[0]?.start ? `${shifts[0].start}:00` : null,
              shift1_end: shifts[0]?.end ? `${shifts[0].end}:00` : null,
              shift2_start: shifts[1]?.start ? `${shifts[1].start}:00` : null,
              shift2_end: shifts[1]?.end ? `${shifts[1].end}:00` : null,
              shift3_start: shifts[2]?.start ? `${shifts[2].start}:00` : null,
              shift3_end: shifts[2]?.end ? `${shifts[2].end}:00` : null,
            };

            if (shouldIncludeRtspUrl(modal.data.rtsp_url)) {
              payload.rtsp_url = modal.data.rtsp_url.trim();
            }

            const createdCamera = await createCamera(parentId, payload);
            if (createdCamera?.id) {
              await saveVideoSettings(createdCamera.id, buildLiveArchivePayload(modal.data, shouldIncludeRtspUrl));
              await getVideoSettings(createdCamera.id).catch(() => null);
            }
            cacheRef.current.cameras.delete(parentId);
            break;
          }
          default:
            break;
        }
      } else {
        switch (modal.level) {
          case 'factories':
            await updateFactory(modal.originalItem.id, {
              name: modal.data.name,
              image: modal.data.image_url,
              owner: modal.data.owner,
            });
            cacheRef.current.factories = null;
            break;
          case 'workshops':
            await updateWorkshop(modal.originalItem.id, {
              name: modal.data.name,
              image: modal.data.image_url,
              owner: modal.data.owner,
            });
            cacheRef.current.workshops.delete(parentId);
            break;
          case 'sections':
            await updateSection(modal.originalItem.id, {
              name: modal.data.name,
              image: modal.data.image_url,
              owner: modal.data.owner,
            });
            cacheRef.current.sections.delete(parentId);
            break;
          case 'rooms':
            await updateRoom(modal.originalItem.id, {
              name: modal.data.name,
              image: modal.data.image_url,
              owner: modal.data.owner,
            });
            cacheRef.current.rooms.delete(parentId);
            break;
          case 'cameras': {
            // ТЗ №33: payload без id, detection_configs — строкой с детекторами
            // самой камеры (форма их не меняет), RTSP — только новый адрес.
            // ADMIN-ZONE-SYNC01: зоны из формы не отправляются. Restart не вызывается.
            const cameraId = modal.originalItem.id;
            // Профиль детекции в этой же форме сохраняется сразу, отдельным
            // запросом. Детекторы берутся из свежей камеры, иначе сохранение
            // формы вернуло бы значения на момент её открытия.
            const freshCamera = await getCamera(cameraId).catch(() => null);
            const payload = buildCameraUpdatePayload({
              data: modal.data,
              original: freshCamera
                ? { ...modal.originalItem, detection_configs: freshCamera.detection_configs }
                : modal.originalItem,
              roomId: parentId,
              shouldIncludeRtspUrl,
            });

            await updateCamera(cameraId, payload);
            await saveVideoSettings(cameraId, buildLiveArchivePayload(modal.data, shouldIncludeRtspUrl));
            await getVideoSettings(cameraId).catch(() => null);
            cacheRef.current.cameras.delete(parentId);
            refetchCameraId = cameraId;
            break;
          }
          default:
            break;
        }
      }

      // Поля уже сохранены; изображение — отдельный запрос. Его сбой не
      // выдаётся за успех: форма остаётся открытой с повтором только загрузки.
      let imageFailure = '';
      if (modal.level !== 'cameras' && hasImageChange(modal.data)) {
        if (savedEntity?.id == null) {
          imageFailure = 'Откройте объект заново и загрузите изображение.';
        } else {
          try {
            await applyImageChange(levelMap[modal.level], savedEntity.id, modal.data);
          } catch (imageError) {
            imageFailure = describeEntityImageError(imageError);
          }
        }
      }

      setLoading(true);
      try {
        await reloadCurrentLevelData();
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }

      if (imageFailure) {
        setModal((prev) => ({
          ...prev,
          mode: 'edit',
          originalItem: savedEntity?.id != null ? { ...(prev.originalItem || {}), ...savedEntity } : prev.originalItem,
          errors: { ...prev.errors, image: `Остальные поля сохранены, изображение нет. ${imageFailure}` },
        }));
        return;
      }

      // ТЗ №33: после сохранения камера перечитывается целиком — карточка
      // показывает то, что сохранил backend, а не то, что было в форме.
      if (refetchCameraId != null) {
        try {
          const fresh = await getCamera(refetchCameraId);
          if (fresh) {
            setCurrentData((prev) => prev.map((camera) => (camera.id === refetchCameraId ? fresh : camera)));
          }
        } catch (error) {
          console.error(error);
        }
      }

      handleModalClose();
    } catch (error) {
      // ADMIN-ZONE-SYNC01: удаление legacy monitoring_zone через generic PATCH
      // теперь запрещено бэкендом — это не техническая ошибка, а подсказка
      // пользователю пойти в редактор зон. Модалка при этом не закрывается,
      // а сырой JSON ответа больше никогда не показывается пользователю.
      const conflict = describeConflict(error, 'zone');
      if (conflict.debug === 'legacy_zone_delete_requires_zones_api') {
        setModal((prev) => ({ ...prev, errors: { ...prev.errors, form: conflict.message } }));
      } else {
        alert(`Ошибка: ${describeHttpError(error, 'Ошибка при сохранении')}`);
      }
    } finally {
      setModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleImageFileChange = (file) => {
    setModal((prev) => ({
      ...prev,
      data: { ...prev.data, image_file: file, image_remove: file ? false : prev.data.image_remove },
      errors: { ...prev.errors, image: undefined },
    }));
  };

  const handleImageRemoveChange = (remove) => {
    setModal((prev) => ({
      ...prev,
      data: { ...prev.data, image_remove: remove, image_file: remove ? null : prev.data.image_file },
      errors: { ...prev.errors, image: undefined },
    }));
  };

  /** Повтор только загрузки/удаления изображения — поля формы уже сохранены. */
  const handleImageRetry = async () => {
    const entityId = modal.originalItem?.id;
    if (entityId == null || !hasImageChange(modal.data)) return;
    setModal((prev) => ({ ...prev, loading: true }));
    try {
      await applyImageChange(levelMap[modal.level], entityId, modal.data);
    } catch (imageError) {
      setModal((prev) => ({
        ...prev,
        loading: false,
        errors: { ...prev.errors, image: `Остальные поля сохранены, изображение нет. ${describeEntityImageError(imageError)}` },
      }));
      return;
    }
    setLoading(true);
    try {
      await reloadCurrentLevelData();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
    handleModalClose();
  };

  const [accessTarget, setAccessTarget] = useState(null);

  const handleAccessClick = (item, event) => {
    event?.stopPropagation?.();
    setAccessTarget({ entityType: levelMap[currentLevel], id: item.id, name: item.name });
  };

  const handleDeleteClick = async (item, event) => {
    event.stopPropagation();
    if (!window.confirm(`Удалить «${item.name}»?`)) return;

    if (currentLevel === 'cameras') {
      try {
        const result = await deleteCamera(item.id);
        setCurrentData((prev) =>
          prev.map((camera) =>
            camera.id === item.id
              ? {
                  ...camera,
                  status: result.status,
                  status_before_delete: result.status_before_delete,
                  delete_requested_at: result.delete_requested_at,
                  delete_scheduled_at: result.delete_scheduled_at,
                }
              : camera
          )
        );
        const roomId = selectedItems.room?.id;
        if (roomId != null) cacheRef.current.cameras.delete(roomId);
      } catch {
        alert('Ошибка при удалении');
      }
      return;
    }

    try {
      const parentId = getParentId();

      switch (currentLevel) {
        case 'factories':
          await deleteFactory(item.id);
          cacheRef.current.factories = null;
          break;
        case 'workshops':
          await deleteWorkshop(item.id);
          cacheRef.current.workshops.delete(parentId);
          break;
        case 'sections':
          await deleteSection(item.id);
          cacheRef.current.sections.delete(parentId);
          break;
        case 'rooms':
          await deleteRoom(item.id);
          cacheRef.current.rooms.delete(parentId);
          break;
        default:
          break;
      }

      setLoading(true);
      try {
        await reloadCurrentLevelData();
      } finally {
        setLoading(false);
      }
    } catch {
      alert('Ошибка при удалении');
    }
  };

  // ТЗ №26: «Камеры локации» — раздел «Камеры» сразу на этой локации.
  const handleRoomCamerasClick = (room, event) => {
    event.stopPropagation();
    openLocationInCameras(navigate, {
      factory: selectedItems.factory,
      workshop: selectedItems.workshop,
      section: selectedItems.section,
      room: { id: room.id, name: room.name },
    });
  };

  // ТЗ №26: открыть правку карточки, на которую вёл переход из «Камер»,
  // либо подсветить её (FE-002). State перехода сразу очищается, чтобы reload
  // не повторял переход. Нет карточки (удалена, нет доступа, 403/404) — одна
  // нейтральная фраза вместо молчания.
  useEffect(() => {
    const target = pendingEditRef.current;
    if (!target) return;
    if (target.level !== currentLevel) {
      pendingEditRef.current = null;
      return;
    }
    if (loading || !levelLoaded) return;
    pendingEditRef.current = null;
    navigate(location.pathname, { replace: true, state: null });
    const item = currentData.find((entry) => String(entry.id) === String(target.id));
    if (!item) {
      setTargetNotice({
        level: target.level,
        text: target.level === 'cameras' ? 'Камера недоступна или удалена' : 'Локация недоступна или удалена',
      });
      return;
    }
    if (target.mode === 'highlight') {
      setHighlight({ level: target.level, id: item.id });
      requestAnimationFrame(() => {
        document.querySelector(`[data-admin-item-id="${item.id}"]`)
          ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      });
      return;
    }
    handleEditClick(item);
    // handleEditClick пересоздаётся на каждый рендер; эффект должен
    // срабатывать только по загрузке данных уровня.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData, currentLevel, loading, levelLoaded]);

  // FE-CORR-07 FE-004: превью камеры открывает её кадры в разделе по типу.
  const handleOpenFramesClick = (item, event) => {
    event.stopPropagation();
    openCameraFramesFromAdmin(navigate, item, selectedItems);
  };
  const canOpenFrames = (item) => isNavigationKeyAllowed(
    navigationKeys,
    isPeopleLikeCameraType(item.camera_type || item.type) ? NAV_KEYS.MONITORING_FRAMES : NAV_KEYS.MONITORING_EQUIPMENT,
  );

  // Диагностика камеры доступна только из админки (страница CameraDetailPage).
  const handleDiagnosticsClick = (item, event) => {
    event.stopPropagation();
    const base = isPeopleLikeCameraType(item.camera_type) ? 'cameras' : 'equipment';
    navigate(`/${base}/${item.id}/diagnostics`);
  };

  const handleRestoreClick = async (item, event) => {
    event.stopPropagation();
    setRestoringCamera(item.id);

    try {
      const result = await cancelDeleteCamera(item.id);
      setCurrentData((prev) =>
        prev.map((camera) =>
          camera.id === item.id
            ? {
                ...camera,
                status: result.restored_status,
                status_before_delete: null,
                delete_requested_at: null,
                delete_scheduled_at: null,
                delete_cancelled_at: new Date().toISOString(),
              }
            : camera
        )
      );
      const roomId = selectedItems.room?.id;
      if (roomId != null) cacheRef.current.cameras.delete(roomId);
      alert('Камера восстановлена');
    } catch {
      alert('Не удалось восстановить камеру');
    } finally {
      setRestoringCamera(null);
    }
  };

  return (
    <div className="admins-page">
      <div className="page-header">
        <button className="back-button" onClick={handleBack} disabled={currentLevel === 'factories'}>
          <ArrowLeft className="g-icon-lead" width={16} height={16} />Назад
        </button>
        <h1>Администрирование</h1>
        <div className="header-actions">
          <button className="create-button" onClick={handleCreateClick}>
            + Создать {levelLabels[currentLevel]}
          </button>
        </div>
      </div>

      <AdminBreadcrumbNav
        selectedItems={selectedItems}
        onNavigate={handleBreadcrumbClick}
      />

      <div className="page-content">
        {targetNotice?.level === currentLevel && (
          <div className="admin-target-notice" role="status">{targetNotice.text}</div>
        )}
        {cameraFilterActive && !loading && currentData.length > 0 && (
          <CameraStatusFilter
            cameras={currentData}
            shown={visibleData.length}
            selected={statusGroups}
            onChange={setStatusGroups}
          />
        )}
        {cameraFilterActive && !loading && currentData.length > 0 && visibleData.length === 0 && (
          <div className="csf-empty" role="status">
            {statusGroups.length === 0
              ? 'Не выбрано ни одного состояния камер'
              : 'Камер в выбранных состояниях нет'}
            <button type="button" className="csf-link" onClick={() => setStatusGroups(ALL_CAMERA_STATUS_GROUPS)}>
              Показать все
            </button>
          </div>
        )}
        <AdminItemGrid
          loading={loading}
          currentData={visibleData}
          currentLevel={currentLevel}
          cameraImages={cameraImages}
          levelMap={levelMap}
          managingCamera={managingCamera}
          restoringCamera={restoringCamera}
      ownerMap={ownerMap}
          getStatusColor={getStatusColor}
          getStatusText={getStatusText}
          onItemClick={handleItemClick}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          onCameraAction={handleCameraAction}
          onRestoreClick={handleRestoreClick}
          onDiagnosticsClick={handleDiagnosticsClick}
          onRoomCamerasClick={handleRoomCamerasClick}
          onAccessClick={handleAccessClick}
          onOpenFramesClick={handleOpenFramesClick}
          canOpenFrames={canOpenFrames}
          highlightId={highlight?.level === currentLevel ? highlight.id : null}
        />
      </div>

      {accessTarget && (
        <AccessGrantsModal
          entityType={accessTarget.entityType}
          entityId={accessTarget.id}
          entityName={accessTarget.name}
          hasNested={accessTarget.entityType !== 'camera'}
          onClose={() => setAccessTarget(null)}
        />
      )}

      {modal.isOpen && (
        <AdminModalForm
          modal={modal}
          levelLabels={levelLabels}
          availableOwners={availableOwners}
          isPeopleLikeCameraType={isPeopleLikeCameraType}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          onFormChange={handleFormChange}
          onTypeChange={handleTypeChange}
          onShiftChange={handleShiftChange}
          onAddShift={handleAddShift}
          onRemoveShift={handleRemoveShift}
          onResumeArchive={handleResumeArchive}
          onImageFileChange={handleImageFileChange}
          onImageRemoveChange={handleImageRemoveChange}
          onRetryImage={modal.errors?.image ? handleImageRetry : undefined}
        />
      )}
    </div>
  );
};

export default AdminPage;
